import React, { useEffect, useState } from "react";
import { Image, Stack } from "react-bootstrap";

import "./AllowLocationManuallyOnIos.scss";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
  WebApiPermissionStates,
} from "../../utils/constants/generic-types";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImageStepOne from "../../images/step-one.png";
import ImageStepTwo from "../../images/step-two.png";
import ImageAllowLocationOnIosExplainer from "../../images/allow-location-on-ios-explainer.gif";
import ImageAllowLocationServicesExplainer from "../../images/allow-location-services-explainer.gif";
import ImageAllowSafariLocationExplainer from "../../images/allow-safari-location-explainer.gif";
import SecondaryActionButton from "../Buttons/SecondaryActionButton/SecondaryActionButton";
import { checkBrowser } from "../../utils/helpers";
import useDisclosure from "../../hooks/useDisclosure";
import { HandleGetDeviceLocationInfo } from "../topup/TopupFlow/types";
import { showErrorToastDanger } from "../../utils/show-toast";
import { useTranslation } from "react-i18next";
import { CustomSpinner } from "../CustomSpinner/CustomSpinner";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

interface AllowLocationManuallyOnIosProps extends GenericModalProps {
  onOpen: FunctionWithVoidReturn;
  handleDeviceLocationCheck: HandleGetDeviceLocationInfo;
  handleSuccessfulPrelimTopupChecks?: FunctionWithVoidReturn;
}

interface AllowLocationServicesProps extends Omit<
  AllowLocationManuallyOnIosProps,
  "onOpen"
> {
  openSecondStep: FunctionWithVoidReturn;
}

interface AllowSafariLocationProps extends Omit<
  AllowLocationManuallyOnIosProps,
  "onOpen"
> {
  openFirstStep: FunctionWithVoidReturn;
}

export default function AllowLocationManuallyOnIos({
  isOpen,
  onOpen,
  onClose,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
}: AllowLocationManuallyOnIosProps) {
  const browser = checkBrowser();
  const browserIsSafari = !!browser?.match(/Safari/i);

  if (browserIsSafari) {
    return (
      <AllowLocationManuallyOnSafari
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        handleDeviceLocationCheck={handleDeviceLocationCheck}
        handleSuccessfulPrelimTopupChecks={handleSuccessfulPrelimTopupChecks}
      />
    );
  }
  return <AllowLocationOnOtherIosBrowsers isOpen={isOpen} onClose={onClose} />;
}

function AllowLocationOnOtherIosBrowsers({
  isOpen,
  onClose,
}: GenericModalProps) {
  const { t } = useTranslation("common", { keyPrefix: "location" });

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="AllowLocationOnOtherIosBrowsers"
    >
      <Stack gap={4} className="pt-4">
        <Stack gap={2}>
          <div className="mb-2 align-self-center IosExplainerImage">
            <Image
              src={ImageAllowLocationOnIosExplainer}
              width={240}
              alt={t("allow_location_on_ios_explainer")}
            />
          </div>
          <h1 className="Grotesk Heading">{t("allow_location_services")}</h1>
          <ul className="ps-0 mb-0 InstructionSteps">
            <li className="Grotesk InstructionStep">
              <span className="StepCount">{t("step_1")}:</span>{" "}
              {t("go_to_settings_browser")}
            </li>
            <li className="Grotesk InstructionStep">
              <span className="StepCount">{t("step_2")}:</span>{" "}
              {t("tap_location_select_allow_while_using_the_app")}
            </li>
          </ul>
          <p className="Grotesk FinalTip">
            {t("once_done_return_to_the_site_to_continue")}!
          </p>
        </Stack>

        <PrimaryActionButton text={t("okay")} onClick={onClose} />
      </Stack>
    </BottomDrawer>
  );
}

function AllowLocationManuallyOnSafari({
  isOpen,
  onOpen,
  onClose,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
}: AllowLocationManuallyOnIosProps) {
  const [queryParams] = useSearchParams();
  const locationModalStep = queryParams.get("locationModalStep");
  const {
    isOpen: isSecondStepOpen,
    onOpen: openSecondStep,
    onClose: closeSecondStep,
  } = useDisclosure();

  useEffect(() => {
    if (locationModalStep === "1") onOpen();
    else if (locationModalStep === "2") openSecondStep();
  }, [locationModalStep, onOpen, openSecondStep]);

  return (
    <>
      <AllowLocationServices
        isOpen={isOpen}
        onClose={onClose}
        openSecondStep={openSecondStep}
        handleDeviceLocationCheck={handleDeviceLocationCheck}
        handleSuccessfulPrelimTopupChecks={handleSuccessfulPrelimTopupChecks}
      />
      <AllowSafariLocation
        isOpen={isSecondStepOpen}
        onClose={closeSecondStep}
        openFirstStep={onOpen}
        handleDeviceLocationCheck={handleDeviceLocationCheck}
        handleSuccessfulPrelimTopupChecks={handleSuccessfulPrelimTopupChecks}
      />
    </>
  );
}

function AllowLocationServices({
  isOpen,
  onClose,
  openSecondStep,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
}: AllowLocationServicesProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [queryParams] = useSearchParams();
  const retryLocationCheck = queryParams.get("locationModalStep") === "1";
  const { t } = useTranslation("common", { keyPrefix: "location" });
  const [loading, setLoading] = useState<boolean>(retryLocationCheck);

  const handleContinue = () => {
    navigate(`${pathname}?locationModalStep=1`);
    window.location.reload();
  };

  useEffect(() => {
    if (!retryLocationCheck) return;

    handleDeviceLocationCheck(({ locationStatus, canTopup }) => {
      setLoading(false);
      onClose();

      if (locationStatus === WebApiPermissionStates.DENIED) {
        openSecondStep();
        return;
      }
      if (canTopup) {
        handleSuccessfulPrelimTopupChecks?.();
      }
    });
    navigate(pathname);
  }, [
    t,
    retryLocationCheck,
    pathname,
    navigate,
    onClose,
    openSecondStep,
    handleDeviceLocationCheck,
    handleSuccessfulPrelimTopupChecks,
  ]);

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      closeBtnStyles={{ position: "absolute", right: 14, top: 14 }}
      containerClass="p-0 AllowLocationServices"
    >
      <Stack className="align-items-center Header">
        <Image src={ImageStepOne} alt={t("step_1")} className="StepOneImage" />
        <Image
          src={ImageAllowLocationServicesExplainer}
          width={200}
          alt={t("allow_location_services_explainer")}
          className="AllowLocationServicesExplainer"
        />
      </Stack>

      <Stack className="MainContent">
        <Stack gap={2} className="align-items-center">
          <h1 className="text-capitalize text-center Hanson-700 SpecialFontSettings Heading">
            {t("allow_location_access")}
          </h1>
          <ul className="ps-0 mb-0 InstructionSteps">
            <li className="Grotesk SpecialFontSettings InstructionStep">
              <span className="fw-bold StepCount">{t("step_1")}:</span>{" "}
              {t("open")}{" "}
              <span className="fw-medium">
                {t(
                  "settings_privacy_and_security_location services_safari_websites"
                )}
              </span>
            </li>
            <li className="Grotesk SpecialFontSettings InstructionStep">
              <span className="fw-bold StepCount">{t("step_2")}:</span>{" "}
              {t("select")}{" "}
              <span className="fw-bold">{t("while_using_the_app")}</span>
            </li>
          </ul>
        </Stack>
        <PrimaryActionButton
          text={
            !loading ? (
              t("i_have_turned_on_location_services")
            ) : (
              <CustomSpinner size="sm" />
            )
          }
          onClick={handleContinue}
        />
      </Stack>
    </BottomDrawer>
  );
}

function AllowSafariLocation({
  isOpen,
  onClose,
  openFirstStep,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
}: AllowSafariLocationProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [queryParams] = useSearchParams();
  const retryLocationCheck = queryParams.get("locationModalStep") === "2";
  const { t } = useTranslation("common", { keyPrefix: "location" });
  const [loading, setLoading] = useState<boolean>(retryLocationCheck);

  const handleContinue = () => {
    navigate(`${pathname}?locationModalStep=2`);
    window.location.reload();
  };

  const goToStepOne = () => {
    onClose();
    openFirstStep();
  };

  useEffect(() => {
    if (!retryLocationCheck) return;

    handleDeviceLocationCheck(({ locationStatus, canTopup }) => {
      setLoading(false);

      if (locationStatus === WebApiPermissionStates.DENIED) {
        showErrorToastDanger(
          t("unfortunately_we_could_not_access_your_location_info")
        );
      }
      if (canTopup) {
        handleSuccessfulPrelimTopupChecks?.();
        onClose();
      }
    });
    navigate(pathname);
    onClose();
  }, [
    t,
    retryLocationCheck,
    pathname,
    navigate,
    onClose,
    handleDeviceLocationCheck,
    handleSuccessfulPrelimTopupChecks,
  ]);

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      closeBtnStyles={{ position: "absolute", right: 14, top: 14 }}
      containerClass="p-0 AllowSafariLocation"
    >
      <Stack className="align-items-center Header">
        <Image src={ImageStepTwo} alt={t("step_1")} className="StepTwoImage" />
        <Image
          src={ImageAllowSafariLocationExplainer}
          width={200}
          alt={t("allow_safari_location_explainer")}
          className="AllowSafariLocationExplainer"
        />
      </Stack>

      <Stack className="MainContent">
        <Stack gap={2} className="align-items-center">
          <h1 className="text-capitalize text-center Hanson-700 SpecialFontSettings Heading">
            {t("allow_safari_to_use_your_location")}
          </h1>
          <ul className="ps-0 mb-0 InstructionSteps">
            <li className="Grotesk SpecialFontSettings InstructionStep">
              <span className="fw-bold StepCount">{t("step_1")}:</span>{" "}
              {t("in")} <span className="fw-bold">{t("settings")},</span>{" "}
              {t("scroll_down_to")}{" "}
              <span className="fw-bold">{t("safari_location")}</span>
            </li>
            <li className="Grotesk SpecialFontSettings InstructionStep">
              <span className="fw-bold StepCount">{t("step_2")}:</span>{" "}
              {t("select")} <span className="fw-bold">{t("allow")}</span>
            </li>
          </ul>
        </Stack>
        <Stack gap={3}>
          <PrimaryActionButton
            text={
              !loading ? (
                t("i_have_allowed_location_access")
              ) : (
                <CustomSpinner size="sm" />
              )
            }
            onClick={handleContinue}
          />
          <SecondaryActionButton
            text={t("go_back_to_step_one")}
            onClick={goToStepOne}
          />
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}
